import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import {
  selectAllMsaJobs,
  setMsaJobStarted,
  updateMsaProgress,
  completeMsaCalculation,
  failMsaCalculation,
} from "../features/msa/msaSlice";
import { getMsaEventStreamUrl } from "../services/msaApiService";
import type {
  MsaJobStartedEvent,
  MsaJobProgressEvent,
  MsaJobCompletedEvent,
  MsaJobFailedEvent,
  MsaMode,
} from "../features/msa/msaTypes";

/**
 * Hook to manage SSE subscriptions for MSA calculations
 * Runs at App.tsx level to persist connections across navigation
 * Watches Redux state for job_id changes and automatically subscribes
 */
export const useMsaEventSubscription = () => {
  const dispatch = useAppDispatch();
  const allJobs = useAppSelector(selectAllMsaJobs);

  // Track active EventSource connections
  // Key format: `${projectId}_${mode}_${jobId}`
  const eventSourcesRef = useRef<Map<string, EventSource>>(new Map());
  
  // Track which job IDs we've already subscribed to (prevents duplicates)
  const subscribedJobIdsRef = useRef<Set<string>>(new Set());

  // Main effect: Subscribe to new jobs when they appear in Redux
  useEffect(() => {
    // Extract current job IDs that need subscriptions
    const currentJobs: Array<{
      projectId: string;
      mode: MsaMode;
      jobId: string;
    }> = [];

    Object.entries(allJobs).forEach(([projectId, projectJobs]) => {
      if (
        projectJobs.baseline?.jobId &&
        projectJobs.baseline.status !== "completed" &&
        projectJobs.baseline.status !== "failed"
      ) {
        currentJobs.push({
          projectId,
          mode: "baseline",
          jobId: projectJobs.baseline.jobId,
        });
      }
      if (
        projectJobs.project?.jobId &&
        projectJobs.project.status !== "completed" &&
        projectJobs.project.status !== "failed"
      ) {
        currentJobs.push({
          projectId,
          mode: "project",
          jobId: projectJobs.project.jobId,
        });
      }
    });

    // Subscribe to NEW jobs only (not already subscribed)
    currentJobs.forEach(({ projectId, mode, jobId }) => {
      // Skip if already subscribed to this job ID
      if (subscribedJobIdsRef.current.has(jobId)) {
        return;
      }

      const key = `${projectId}_${mode}_${jobId}`;
      console.log(`Creating SSE connection for ${projectId}_${mode} with job ID: ${jobId}`);

      const eventSourceUrl = getMsaEventStreamUrl(jobId);
      const eventSource = new EventSource(eventSourceUrl);
      
      // Mark as subscribed
      subscribedJobIdsRef.current.add(jobId);

      // Handle job_started event
      eventSource.addEventListener("job_started", (event) => {
        console.log(`job_started event for ${projectId}_${mode}:`, event.data);
        try {
          const data: MsaJobStartedEvent = JSON.parse(event.data);
          dispatch(
            setMsaJobStarted({
              projectId,
              mode,
              message: data.message,
            })
          );
        } catch (error) {
          console.error("Error parsing job_started event:", error);
        }
      });

      // Handle job_progress event
      eventSource.addEventListener("job_progress", (event) => {
        console.log(`job_progress event for ${projectId}_${mode}:`, event.data);
        try {
          const data: MsaJobProgressEvent = JSON.parse(event.data);
          dispatch(
            updateMsaProgress({
              projectId,
              mode,
              progress: data.data.progress,
              message: data.data.message,
            })
          );
        } catch (error) {
          console.error("Error parsing job_progress event:", error);
        }
      });

      // Handle job_completed event
      eventSource.addEventListener("job_completed", (event) => {
        console.log(`job_completed event for ${projectId}_${mode}:`, event.data);
        try {
          const data: MsaJobCompletedEvent = JSON.parse(event.data);
          dispatch(
            completeMsaCalculation({
              projectId,
              mode,
              result: data.data.result,
            })
          );
          // Close the connection after completion
          console.log(`Closing completed SSE connection for ${key}`);
          eventSource.close();
          eventSourcesRef.current.delete(key);
          subscribedJobIdsRef.current.delete(jobId);
        } catch (error) {
          console.error("Error parsing job_completed event:", error);
        }
      });

      // Handle job_failed event
      eventSource.addEventListener("job_failed", (event) => {
        console.log(`job_failed event for ${projectId}_${mode}:`, event.data);
        try {
          const data: MsaJobFailedEvent = JSON.parse(event.data);
          dispatch(
            failMsaCalculation({
              projectId,
              mode,
              error: data.data.message || "Unknown error occurred",
            })
          );
          // Close the connection after failure
          console.log(`Closing failed SSE connection for ${key}`);
          eventSource.close();
          eventSourcesRef.current.delete(key);
          subscribedJobIdsRef.current.delete(jobId);
        } catch (error) {
          console.error("Error parsing job_failed event:", error);
        }
      });

      // Handle connection open
      eventSource.addEventListener("open", () => {
        console.log(`SSE connection opened for ${projectId}_${mode}`);
      });

      // Handle connection errors
      eventSource.addEventListener("error", (event) => {
        console.error(`SSE connection error for ${projectId}_${mode}:`, event);
        // EventSource will automatically try to reconnect
        // Log for debugging purposes
      });

      // Store the event source
      eventSourcesRef.current.set(key, eventSource);
    });

    // NO cleanup here - we don't want to close connections on every Redux update!
  }, [allJobs, dispatch]);

  // Separate effect ONLY for component unmount cleanup
  useEffect(() => {
    return () => {
      console.log("Component unmounting - cleaning up all SSE connections");
      eventSourcesRef.current.forEach((eventSource, key) => {
        console.log(`Closing SSE connection for ${key}`);
        eventSource.close();
      });
      eventSourcesRef.current.clear();
      subscribedJobIdsRef.current.clear();
    };
  }, []); // Empty deps = only runs on mount/unmount

  // This hook doesn't return anything - it's a side-effect only hook
};

